//@flow
import React, {useState} from 'react';
import {makeStyles, Typography} from '@material-ui/core';

import {useToastActions} from '../../modules/toast-alert/utils';
import {uploadImageService} from '../../modules/file-storage/promises';
import InlineLoader from '../LoadingSpinner/InlineLoader';

type TemplateMedia = {
  id: string,
  type: 'IMAGE' | 'VIDEO' | 'DOCUMENT',
  scope: 'HEADER',
  value: string,
};

type Props = {
  title: string,
  media: TemplateMedia[],
  onMediaChange: (id: string, value: string) => void,
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  headerText: {
    fontSize: '12px',
    fontWeight: 600,
  },
  inputSection: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50%',
    height: '80px',
    minWidth: '200px',
    border: '2px dashed #E0E0E0',
    borderRadius: '4px',
    padding: '12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
  fileName: {
    width: '100%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '150px',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    zIndex: 1,
  },
}));

const getMediaLabel = (format: string) => {
  switch (format) {
    case 'IMAGE':
      return 'Subir imagen';
    case 'VIDEO':
      return 'Subir video';
    case 'DOCUMENT':
      return 'Subir documento';
    default:
      return 'Subir archivo';
  }
};

const getAcceptType = (format: string) => {
  switch (format) {
    case 'IMAGE':
      return 'image/*';
    case 'VIDEO':
      return 'video/*';
    case 'DOCUMENT':
      return 'application/pdf';
    default:
      return 'application/pdf';
  }
};

export default function MediaSection({title, media, onMediaChange}: Props) {
  const classes = useStyles();

  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean,
  }>({});
  const [selectedFiles, setSelectedFiles] = useState<{[key: string]: File}>({});

  const {showToast} = useToastActions();

  const handleFileChange = (mediaItem: TemplateMedia) => e => {
    const file = e.target.files[0];
    if (file.size > 1000000) {
      showToast({
        title: 'Tamaño de archivo excedido',
        message: 'El archivo debe pesar menos de 1MB',
        type: 'danger',
      });
      return;
    }
    setSelectedFiles(prev => ({...prev, [mediaItem.id]: file}));
    setUploadingFiles(prev => ({...prev, [mediaItem.id]: true}));

    uploadImageService(file, 'winehouse/crm-campaigns', {
      temp: true,
    })
      .then(response => {
        onMediaChange(mediaItem.id, response[0].url);
      })
      .catch(error => {
        showToast({
          title: 'Error al subir el archivo',
          message: 'Inténtalo de nuevo por favor',
          type: 'danger',
        });
      })
      .finally(() => {
        setUploadingFiles(prev => ({...prev, [mediaItem.id]: false}));
      });
  };

  if (media.length === 0) {
    return null;
  }

  return (
    <div className={classes.container}>
      <header style={{display: 'flex', gap: '.5rem'}}>
        <Typography className={classes.headerText}>{title}</Typography>
        <Typography variant="caption">(Opcional)</Typography>
      </header>
      {media.map(mediaItem => {
        const acceptType = getAcceptType(mediaItem.type);
        const isUploading = uploadingFiles[mediaItem.id];
        const selectedFile = selectedFiles[mediaItem.id];

        return (
          <label key={mediaItem.id} className={classes.inputSection}>
            <input
              type="file"
              name="file"
              accept={acceptType}
              hidden
              onChange={handleFileChange(mediaItem)}
            />
            {isUploading && (
              <span className={classes.loader}>
                <InlineLoader size={24} loading />
              </span>
            )}
            {selectedFile ? (
              <Typography variant="body2" className={classes.fileName}>
                {selectedFile.name}
              </Typography>
            ) : (
              <Typography variant="body2">
                {getMediaLabel(mediaItem.type)}
              </Typography>
            )}
          </label>
        );
      })}
    </div>
  );
}
